import React, { useEffect, useState } from "react";
import QRCode from "qrcode.react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import {
	Dialog,
	DialogContent,
	Paper,
	Typography,
	Button,
	TextField,
	Box,
	CircularProgress
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
	const [qrCode, setQrCode] = useState("");
	const [mode, setMode] = useState("qr");
	const [phoneInput, setPhoneInput] = useState("");
	const [pairingCode, setPairingCode] = useState("");
	const [requesting, setRequesting] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	useEffect(() => {
		if (open) {
			setMode("qr");
			setPhoneInput("");
			setPairingCode("");
			setRequesting(false);
			setErrorMsg("");
		}
	}, [open]);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;
			try {
				const { data } = await api.get(`/whatsapp/${whatsAppId}`);
				setQrCode(data.qrcode);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	useEffect(() => {
		if (!whatsAppId) return;
		const socket = openSocket();

		socket.on("whatsappSession", data => {
			if (data.action === "update" && data.session.id === whatsAppId) {
				setQrCode(data.session.qrcode);
			}
			if (data.action === "update" && data.session.qrcode === "") {
				onClose();
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [whatsAppId, onClose]);

	const handleRequestPairing = async () => {
		const cleaned = phoneInput.replace(/\D/g, "");
		if (cleaned.length < 11 || cleaned.length > 15) {
			setErrorMsg("Informe o telefone com DDI. Ex: 5521987654321");
			return;
		}
		setErrorMsg("");
		setRequesting(true);
		try {
			const { data } = await api.post(
				`/whatsappsession/${whatsAppId}/pairing-code`,
				{ phoneNumber: cleaned }
			);
			setPairingCode(data.code || "");
			setMode("code");
		} catch (err) {
			toastError(err);
		} finally {
			setRequesting(false);
		}
	};

	const formatPairing = (c) => {
		const s = (c || "").replace(/\s|-/g, "");
		return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4)}` : s;
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" scroll="paper">
			<DialogContent style={{ background: '#ffffff', minWidth: 320 }}>
				<Paper elevation={0} style={{ background: '#ffffff', textAlign: 'center' }}>
					<Typography color="primary" gutterBottom>
						{i18n.t("qrCode.message")}
					</Typography>

					{mode === "qr" && (
						<>
							{qrCode ? (
								<QRCode value={qrCode} size={256} />
							) : (
								<span>Waiting for QR Code</span>
							)}
							<Box mt={2}>
								<Button
									variant="text"
									color="primary"
									size="small"
									onClick={() => setMode("phone")}
								>
									Conectar por código de 8 dígitos
								</Button>
							</Box>
						</>
					)}

					{mode === "phone" && (
						<Box style={{ minWidth: 280, padding: 8 }}>
							<Typography variant="body2" gutterBottom>
								Telefone com DDI (apenas números):
							</Typography>
							<TextField
								fullWidth
								size="small"
								variant="outlined"
								placeholder="5521987654321"
								value={phoneInput}
								onChange={(e) => setPhoneInput(e.target.value)}
								inputProps={{ inputMode: 'tel', maxLength: 15 }}
							/>
							{errorMsg && (
								<Typography variant="caption" color="error">{errorMsg}</Typography>
							)}
							<Box mt={2} display="flex" justifyContent="center" gridGap={8}>
								<Button
									variant="outlined"
									size="small"
									onClick={() => setMode("qr")}
									disabled={requesting}
								>
									Voltar
								</Button>
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={handleRequestPairing}
									disabled={requesting}
								>
									{requesting ? <CircularProgress size={16} /> : "Gerar código"}
								</Button>
							</Box>
						</Box>
					)}

					{mode === "code" && pairingCode && (
						<Box style={{ minWidth: 280, padding: 8 }}>
							<Typography variant="body2" gutterBottom>
								Digite este código no seu WhatsApp:
							</Typography>
							<Typography
								variant="h3"
								style={{
									fontFamily: 'monospace',
									fontWeight: 700,
									letterSpacing: '0.4rem',
									color: '#128C7E',
									margin: '16px 0'
								}}
							>
								{formatPairing(pairingCode)}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								Aparelhos conectados → Conectar com número de telefone
							</Typography>
							<Box mt={2}>
								<Button
									variant="text"
									size="small"
									onClick={() => { setMode("qr"); setPairingCode(""); }}
								>
									Voltar para QR Code
								</Button>
							</Box>
						</Box>
					)}
				</Paper>
			</DialogContent>
		</Dialog>
	);
};

export default React.memo(QrcodeModal);
